import { Box, Button, Stack, Typography } from "@mui/material";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { useCallback, useState } from "react";
import { Controller } from "react-hook-form";
import { FacilitiesDataGrid } from "../components/FacilitiesDataGrid";
import { FacilitiesPickerDialog } from "../components/FacilitiesPickerDialog";
import { TenantPickerSelect } from "../components/TenantPickerSelect";
import { useCommunicationFormContext } from "../hooks/useCommunicationForm";
import { defaultRecipientForm } from "../hooks/useCommunicationForm";
import { CommunicationFacility, RecipientFormType } from "../types";

export const OrganizationStep = () => {
  const { control, watch, setValue, mode } = useCommunicationFormContext();
  const disabled = mode === "view";
  const tenant = watch("tenant");
  const tierIIReports = watch("tierIIReports");
  const documents = watch("communicationDocuments");
  const recipients = watch("recipients");

  const [showFacilityPicker, setShowFacilityPicker] = useState(false);

  const handleFacilityChange = useCallback(
    (newFacilities: CommunicationFacility[]) => {
      const newFacilityIds = newFacilities.map((facility) => facility.id);

      setValue("facilities", newFacilities, {
        shouldValidate: true,
      });

      // invalidate reports that are no longer tied to current facilities
      setValue(
        "tierIIReports",
        tierIIReports.filter((report) =>
          newFacilityIds.includes(report.facilityId)
        ),
        {
          shouldValidate: true,
        }
      );

      // invalidate documents that are no longer tied to current facilities
      setValue(
        "communicationDocuments",
        documents.filter((document) =>
          document.facilities?.some((facility) =>
            newFacilityIds.includes(facility.id)
          )
        ),
        {
          shouldValidate: true,
        }
      );

      // invalidate recipients that are no longer tied to current facilities via lepc or fire department.
      // if all facilities are removed, default to the default recipient.
      const invalidatedRecipients: RecipientFormType[] = [];
      if (newFacilityIds.length > 0) {
        for (const recipient of recipients) {
          if (recipient.lepc) {
            if (
              newFacilities.some(
                (facility) => facility.lepcId === recipient.lepc?.id
              )
            ) {
              invalidatedRecipients.push(recipient);
            }
          } else if (recipient.fireDepartment) {
            if (
              newFacilities.some(
                (facility) =>
                  facility.fireDepartmentId === recipient.fireDepartment?.id
              )
            ) {
              invalidatedRecipients.push(recipient);
            }
          } else {
            invalidatedRecipients.push(recipient);
          }
        }
      }

      setValue(
        "recipients",
        invalidatedRecipients.length
          ? invalidatedRecipients
          : [defaultRecipientForm()],
        {
          shouldValidate: true,
        }
      );
    },
    [tierIIReports, documents, recipients, setValue]
  );

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1">Organization & Facilities</Typography>
      <TenantPickerSelect />
      <Controller
        name="facilities"
        control={control}
        render={({ field: { value }, fieldState: { error } }) => (
          <>
            <Box>
              <Button
                variant="outlined"
                size="large"
                disabled={disabled || !tenant}
                onClick={() => setShowFacilityPicker(true)}
              >
                Select Facilities
              </Button>
            </Box>

            {showFacilityPicker && (
              <FacilitiesPickerDialog
                tenantId={tenant?.id ?? ""}
                initialFacilityIds={value.map((facility) => facility.id)}
                close={() => setShowFacilityPicker(false)}
                saveFacilities={(facilities) => {
                  handleFacilityChange(facilities);
                  setShowFacilityPicker(false);
                }}
              />
            )}

            {value.length > 0 && (
              <FacilitiesDataGrid
                facilityIds={value.map((facility) => facility.id)}
                removeFacility={
                  disabled
                    ? null
                    : (facility) => {
                        handleFacilityChange(
                          value.filter((x) => x.id !== facility.id)
                        );
                      }
                }
              />
            )}

            <ErrorDisplay error={error} />
          </>
        )}
      />
    </Stack>
  );
};
